import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { PermissionButton } from '@/components/ui/permission-button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Play, 
  Pause, 
  Settings, 
  Mail, 
  MessageSquare, 
  Clock, 
  GitBranch, 
  Target, 
  BarChart3, 
  Link2, 
  FormInput,
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Users,
  MousePointer,
  Folder,
  FolderPlus,
  MoreVertical,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  MoveHorizontal,
  Zap
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MarketingPageHeader } from '@/components/marketing/MarketingPageHeader';

// Schema definitions
const workflowSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  type: z.enum(['email_drip', 'sms_drip', 'web_to_lead']),
  triggerType: z.enum(['form_fill', 'date_based', 'link_click', 'manual']),
  triggerData: z.any().optional(),
});

const stepSchema = z.object({
  stepType: z.enum(['email', 'sms', 'delay', 'condition', 'update_field']),
  stepData: z.any(),
  delayAmount: z.number().optional(),
  delayUnit: z.enum(['minutes', 'hours', 'days']).optional(),
  abTestVariant: z.enum(['A', 'B']).optional(),
  abTestPercentage: z.number().min(0).max(100).optional(),
});


const triggerLinkSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().min(1, 'Slug is required'),
  description: z.string().optional(),
  workflowId: z.string().optional(),
  mergeFields: z.any().optional(),
});

type Workflow = {
  id: string;
  name: string;
  description?: string;
  type: string;
  folderId?: string;
  isActive: boolean;
  triggerType: string;
  triggerData?: any;
  createdAt: string;
  updatedAt: string;
  steps?: WorkflowStep[];
};

type WorkflowStep = {
  id: string;
  workflowId: string;
  stepOrder: number;
  stepType: string;
  stepData?: any;
  delayAmount?: number;
  delayUnit?: string;
  isActive: boolean;
  abTestVariant?: string;
  abTestPercentage?: number;
};

type LeadForm = {
  id: string;
  name: string;
  title: string;
  description?: string;
  fields: any;
  submitText: string;
  successMessage: string;
  redirectUrl?: string;
  workflowId?: string;
  isActive: boolean;
  createdAt: string;
};

type TriggerLink = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  workflowId?: string;
  mergeFields?: any;
  isActive: boolean;
  clickCount: number;
  createdAt: string;
};

export default function Workflows() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [isCreateLinkOpen, setIsCreateLinkOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<TriggerLink | null>(null);
  
  // Folder management state
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [folderForm, setFolderForm] = useState({ name: "", description: "" });
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [selectedWorkflowForMove, setSelectedWorkflowForMove] = useState<Workflow | null>(null);
  // Folder navigation state (like Email Marketing Templates)
  const [currentFolderView, setCurrentFolderView] = useState<any>(null);
  
  // Campaign sorting state
  const [campaignSortField, setCampaignSortField] = useState<'name' | 'type' | 'status' | 'createdAt' | null>(null);
  const [campaignSortDirection, setCampaignSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Trigger Links sorting state
  const [linkSortField, setLinkSortField] = useState<'name' | 'slug' | 'status' | 'clicks' | 'createdAt' | null>(null);
  const [linkSortDirection, setLinkSortDirection] = useState<'asc' | 'desc'>('asc');

  // Queries
  const { data: workflows = [] } = useQuery<Workflow[]>({
    queryKey: ['/api/workflows/workflows'],
  });

  const { data: folders = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/folders'],
  });


  const { data: triggerLinks = [] } = useQuery<TriggerLink[]>({
    queryKey: ['/api/workflows/trigger-links'],
  });

  const { data: emailTemplates = [] } = useQuery({
    queryKey: ['/api/workflows/templates/email'],
  });

  const { data: smsTemplates = [] } = useQuery({
    queryKey: ['/api/workflows/templates/sms'],
  });

  const { data: workflowAnalytics = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/analytics/workflows'],
  });

  const { data: formAnalytics = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/analytics/lead-forms'],
  });

  const { data: triggerLinkAnalytics = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/analytics/trigger-links'],
  });

  const { data: leadForms = [] } = useQuery<any[]>({
    queryKey: ['/api/workflows/lead-forms'],
  });

  // State for selected trigger link in analytics
  const [selectedLinkId, setSelectedLinkId] = useState<string>('all');
  
  // State for selected form in analytics
  const [selectedFormId, setSelectedFormId] = useState<string>('all');
  
  // Pagination state for form analytics
  const [formAnalyticsPage, setFormAnalyticsPage] = useState(1);
  const FORMS_PER_PAGE = 10;

  // Reset page when form filter changes
  useEffect(() => {
    setFormAnalyticsPage(1);
  }, [selectedFormId]);

  // Campaign sorting functions
  const handleCampaignSort = (field: 'name' | 'type' | 'status' | 'createdAt') => {
    if (campaignSortField === field) {
      setCampaignSortDirection(campaignSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setCampaignSortField(field);
      setCampaignSortDirection('asc');
    }
  };

  const sortCampaigns = (campaignsArray: Workflow[]) => {
    if (!campaignSortField) return campaignsArray;

    return [...campaignsArray].sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      switch (campaignSortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'type':
          aValue = a.type.toLowerCase();
          bValue = b.type.toLowerCase();
          break;
        case 'status':
          aValue = a.isActive ? 'active' : 'inactive';
          bValue = b.isActive ? 'active' : 'inactive';
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return campaignSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return campaignSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableTableHead = ({ 
    field, 
    label, 
    className = "" 
  }: { 
    field: 'name' | 'type' | 'status' | 'createdAt'; 
    label: string; 
    className?: string;
  }) => {
    const isSorted = campaignSortField === field;
    return (
      <TableHead 
        className={`cursor-pointer select-none hover:bg-muted/50 ${className}`}
        onClick={() => handleCampaignSort(field)}
        data-testid={`sort-${field}`}
      >
        <div className="flex items-center gap-2">
          {label}
          {isSorted ? (
            campaignSortDirection === 'asc' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          ) : (
            <div className="flex flex-col h-4 w-4 opacity-40">
              <ChevronUp className="h-2.5 w-4 -mb-1" />
              <ChevronDown className="h-2.5 w-4" />
            </div>
          )}
        </div>
      </TableHead>
    );
  };

  // Trigger Links sorting functions
  const handleLinkSort = (field: 'name' | 'slug' | 'status' | 'clicks' | 'createdAt') => {
    if (linkSortField === field) {
      setLinkSortDirection(linkSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLinkSortField(field);
      setLinkSortDirection('asc');
    }
  };

  const sortLinks = (linksArray: TriggerLink[]) => {
    if (!linkSortField) return linksArray;

    return [...linksArray].sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (linkSortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'slug':
          aValue = a.slug.toLowerCase();
          bValue = b.slug.toLowerCase();
          break;
        case 'status':
          aValue = a.isActive ? 'active' : 'inactive';
          bValue = b.isActive ? 'active' : 'inactive';
          break;
        case 'clicks':
          aValue = a.clickCount;
          bValue = b.clickCount;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return linkSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return linkSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableLinkTableHead = ({ 
    field, 
    label, 
    className = "" 
  }: { 
    field: 'name' | 'slug' | 'status' | 'clicks' | 'createdAt'; 
    label: string; 
    className?: string;
  }) => {
    const isSorted = linkSortField === field;
    return (
      <TableHead 
        className={`cursor-pointer select-none hover:bg-muted/50 ${className}`}
        onClick={() => handleLinkSort(field)}
        data-testid={`sort-link-${field}`}
      >
        <div className="flex items-center gap-2">
          {label}
          {isSorted ? (
            linkSortDirection === 'asc' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          ) : (
            <div className="flex flex-col h-4 w-4 opacity-40">
              <ChevronUp className="h-2.5 w-4 -mb-1" />
              <ChevronDown className="h-2.5 w-4" />
            </div>
          )}
        </div>
      </TableHead>
    );
  };

  // Folder mutations
  const createFolderMutation = useMutation({
    mutationFn: async (folder: { name: string; description?: string }) => {
      const response = await apiRequest('POST', '/api/workflows/folders', folder);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/folders'] });
      setIsFolderDialogOpen(false);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder created successfully" });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, ...folder }: { id: string; name: string; description?: string }) => {
      const response = await apiRequest('PUT', `/api/workflows/folders/${id}`, folder);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/folders'] });
      setIsFolderDialogOpen(false);
      setSelectedFolder(null);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder updated successfully" });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/workflows/folders/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/folders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/workflows'] });
      toast({ title: "Folder deleted successfully" });
    },
  });

  const moveWorkflowMutation = useMutation({
    mutationFn: async ({ workflowId, folderId }: { workflowId: string; folderId: string | null }) => {
      const response = await apiRequest('PUT', `/api/workflows/workflows/${workflowId}/folder`, { folderId });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/workflows'] });
      setIsMoveDialogOpen(false);
      setSelectedWorkflowForMove(null);
      toast({ title: "Workflow moved successfully" });
    },
  });

  // Mutations


  const createLinkMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest('/api/workflows/trigger-links', 'POST', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/trigger-links'] });
      setIsCreateLinkOpen(false);
      linkForm.reset();
      toast({ title: 'Success', description: 'Trigger link created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create trigger link', variant: 'destructive' });
    },
  });

  const updateLinkMutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      return apiRequest(`/api/workflows/trigger-links/${id}`, 'PUT', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/trigger-links'] });
      setEditingLink(null);
      linkForm.reset();
      toast({ title: 'Success', description: 'Trigger link updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update trigger link', variant: 'destructive' });
    },
  });

  const triggerWorkflowMutation = useMutation({
    mutationFn: async ({ workflowId, contactData }: { workflowId: string; contactData: any }) => {
      return apiRequest(`/api/workflows/workflows/${workflowId}/trigger`, 'POST', { contactData });
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Workflow triggered successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to trigger workflow', variant: 'destructive' });
    },
  });

  // Form hooks

  const linkForm = useForm({
    resolver: zodResolver(triggerLinkSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
    },
  });

  // Folder handlers
  const openCreateFolder = () => {
    setSelectedFolder(null);
    setFolderForm({ name: "", description: "" });
    setIsFolderDialogOpen(true);
  };

  const openEditFolder = (folder: any) => {
    setSelectedFolder(folder);
    setFolderForm({ name: folder.name, description: folder.description || "" });
    setIsFolderDialogOpen(true);
  };

  const handleFolderSubmit = () => {
    if (selectedFolder) {
      updateFolderMutation.mutate({ id: selectedFolder.id, ...folderForm });
    } else {
      createFolderMutation.mutate(folderForm);
    }
  };

  const handleDeleteFolder = (folderId: string) => {
    if (confirm("Are you sure you want to delete this folder?")) {
      deleteFolderMutation.mutate(folderId);
    }
  };

  const openMoveWorkflow = (workflow: Workflow) => {
    setSelectedWorkflowForMove(workflow);
    setIsMoveDialogOpen(true);
  };

  const handleMoveWorkflow = (folderId: string | null) => {
    if (selectedWorkflowForMove) {
      moveWorkflowMutation.mutate({ 
        workflowId: selectedWorkflowForMove.id, 
        folderId 
      });
    }
  };


  const openEditLink = (link: TriggerLink) => {
    setEditingLink(link);
    linkForm.reset({
      name: link.name,
      slug: link.slug,
      description: link.description || '',
    });
  };

  const closeEditLink = () => {
    setEditingLink(null);
    linkForm.reset();
  };

  const onSubmitLink = (data: any) => {
    if (editingLink) {
      updateLinkMutation.mutate({ id: editingLink.id, ...data });
    } else {
      createLinkMutation.mutate(data);
    }
  };

  const renderWorkflowBuilder = (workflow: Workflow) => {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Workflow Builder - {workflow.name}
          </CardTitle>
          <CardDescription>
            Configure workflow steps, delays, and A/B testing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2">Available Actions</h4>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="justify-start">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                  <Button variant="outline" size="sm" className="justify-start">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Send SMS
                  </Button>
                  <Button variant="outline" size="sm" className="justify-start">
                    <Clock className="h-4 w-4 mr-2" />
                    Add Delay
                  </Button>
                  <Button variant="outline" size="sm" className="justify-start">
                    <GitBranch className="h-4 w-4 mr-2" />
                    A/B Test
                  </Button>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Workflow Settings</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Status</span>
                    <Badge variant={workflow.isActive ? 'default' : 'secondary'}>
                      {workflow.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Trigger</span>
                    <Badge variant="outline">{workflow.triggerType}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Type</span>
                    <Badge variant="outline">{workflow.type}</Badge>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Workflow Steps</h4>
              <div className="text-sm text-muted-foreground mb-4">
                Drag and drop to reorder steps. Configure delays and A/B testing for each step.
              </div>
              {workflow.steps && workflow.steps.length > 0 ? (
                <div className="space-y-2">
                  {workflow.steps.map((step, index) => (
                    <Card key={step.id} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium">
                            {index + 1}
                          </span>
                          <div>
                            <div className="font-medium">
                              {step.stepType === 'email' && 'Send Email'}
                              {step.stepType === 'sms' && 'Send SMS'}
                              {step.stepType === 'delay' && `Wait ${step.delayAmount} ${step.delayUnit}`}
                              {step.stepType === 'condition' && 'Condition Check'}
                              {step.stepType === 'update_field' && 'Update Fields'}
                            </div>
                            {step.abTestVariant && (
                              <div className="text-xs text-muted-foreground">
                                A/B Test Variant {step.abTestVariant} ({step.abTestPercentage}%)
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No steps configured yet</p>
                  <p className="text-sm">Add your first workflow step to get started</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <MarketingPageHeader
        icon={Zap}
        iconColor="bg-yellow-500"
        title="Workflows"
        description="Create automated email and SMS drip campaigns and trigger links"
      />

      <Tabs defaultValue="campaigns" className="space-y-4">
        <TabsList>
          <TabsTrigger value="campaigns">Drip Campaigns</TabsTrigger>
          <TabsTrigger value="links">Trigger Links</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Drip Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Email & SMS Drip Campaigns</h2>
              <p className="text-sm text-muted-foreground">
                Create automated email and SMS sequences with triggers, delays, and A/B testing
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <PermissionButton
                permission="marketing:edit"
                whenDenied="hide"
                variant="outline"
                onClick={openCreateFolder}
              >
                <FolderPlus className="h-4 w-4 mr-2" />
                New Folder
              </PermissionButton>
              <PermissionButton
                permission="marketing:edit"
                whenDenied="hide"
                onClick={() => navigate('/campaign-builder')}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </PermissionButton>
            </div>
          </div>

          <div className="space-y-6">
            {/* Folder View (when viewing a specific folder) */}
            {currentFolderView ? (
              <div className="space-y-4">
                {/* Breadcrumb Navigation */}
                <div className="flex items-center gap-2 text-sm">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentFolderView(null)}
                    className="h-8 px-2"
                  >
                    ← Back to All Folders
                  </Button>
                </div>

                {/* Current Folder Header */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div className="flex items-center gap-2">
                      <Folder className="h-5 w-5 text-blue-600" />
                      <h4 className="text-lg font-medium">{currentFolderView.name}</h4>
                      <Badge variant="secondary">
                        {workflows.filter(w => w.folderId === currentFolderView.id).length} campaigns
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <PermissionButton
                        permission="marketing:edit"
                        whenDenied="hide"
                        variant="outline"
                        size="sm"
                        onClick={() => openEditFolder(currentFolderView)}
                      >
                        <Edit className="h-4 w-4" />
                        Edit Folder
                      </PermissionButton>
                      <PermissionButton
                        permission="marketing:edit"
                        whenDenied="hide"
                        onClick={() => navigate(`/campaign-builder?folderId=${currentFolderView.id}`)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        New Campaign
                      </PermissionButton>
                    </div>
                  </CardHeader>
                  {currentFolderView.description && (
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground">{currentFolderView.description}</p>
                    </CardContent>
                  )}
                </Card>

                {/* Campaigns in Current Folder */}
                {workflows.filter(w => w.folderId === currentFolderView.id).length > 0 ? (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead field="name" label="Campaign Name" />
                          <SortableTableHead field="type" label="Type" />
                          <SortableTableHead field="status" label="Status" />
                          <SortableTableHead field="createdAt" label="Created" />
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortCampaigns(workflows.filter(w => w.folderId === currentFolderView.id)).map((workflow) => (
                          <>
                            <TableRow key={workflow.id} className="hover:bg-muted/50">
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  {workflow.type === 'email_drip' && <Mail className="h-4 w-4 text-muted-foreground" />}
                                  {workflow.type === 'sms_drip' && <MessageSquare className="h-4 w-4 text-muted-foreground" />}
                                  {workflow.type === 'web_to_lead' && <FormInput className="h-4 w-4 text-muted-foreground" />}
                                  {workflow.name}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {workflow.type === 'email_drip' && 'Email Drip'}
                                  {workflow.type === 'sms_drip' && 'SMS Drip'}
                                  {workflow.type === 'web_to_lead' && 'Web to Lead'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={workflow.isActive ? 'default' : 'secondary'}>
                                  {workflow.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </TableCell>
                              <TableCell>{new Date(workflow.createdAt).toLocaleDateString()}</TableCell>
                              <TableCell className="text-right">
                                <TooltipProvider>
                                  <div className="flex items-center justify-end space-x-2">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => openMoveWorkflow(workflow)}
                                          className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                          data-testid={`button-move-${workflow.id}`}
                                        >
                                          <MoveHorizontal className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Move to Campaign Folder</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setSelectedWorkflow(selectedWorkflow?.id === workflow.id ? null : workflow)}
                                          className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                          data-testid={`button-view-steps-${workflow.id}`}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{selectedWorkflow?.id === workflow.id ? 'Hide' : 'View'}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => navigate(`/campaign-builder?id=${workflow.id}`)}
                                          className="hover:!text-white hover:!bg-[#1d4c99]"
                                          data-testid={`button-edit-${workflow.id}`}
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Edit</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                </TooltipProvider>
                              </TableCell>
                            </TableRow>
                            {selectedWorkflow?.id === workflow.id && (
                              <TableRow>
                                <TableCell colSpan={5} className="p-0">
                                  <div className="bg-muted/30 p-6 border-t">
                                    {renderWorkflowBuilder(selectedWorkflow)}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                ) : (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg bg-muted/20">
                    <GitBranch className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">No campaigns in this folder yet</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/campaign-builder?folderId=${currentFolderView.id}`)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Campaign
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Folders Section */}
                {folders.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-md font-medium text-muted-foreground">Folders</h4>
                    <div className="grid gap-3">
                      {folders.map((folder: any) => {
                        const folderWorkflows = workflows.filter(w => w.folderId === folder.id);
                        return (
                          <Card key={folder.id} className="cursor-pointer hover:bg-accent/50 transition-colors">
                            <CardHeader className="pb-3">
                              <div
                                className="flex items-center justify-between"
                                onClick={() => setCurrentFolderView(folder)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                    <Folder className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-medium">{folder.name}</h3>
                                    <p className="text-sm text-muted-foreground">
                                      {folderWorkflows.length} campaign{folderWorkflows.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditFolder(folder);
                                    }}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFolder(folder.id);
                                    }}
                                    disabled={deleteFolderMutation.isPending}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Unorganized Campaigns */}
                {(() => {
                  const unorganizedWorkflows = workflows.filter(w => !w.folderId);
                  if (unorganizedWorkflows.length === 0 && folders.length > 0) return null;
                  
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-muted-foreground">
                          {folders.length > 0 ? "Marketing Campaigns" : "All Campaigns"}
                        </h3>
                        <Badge variant="outline">{unorganizedWorkflows.length}</Badge>
                      </div>
                      
                      {unorganizedWorkflows.length > 0 ? (
                        <Card>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <SortableTableHead field="name" label="Campaign Name" />
                                <SortableTableHead field="type" label="Type" />
                                <SortableTableHead field="status" label="Status" />
                                <SortableTableHead field="createdAt" label="Created" />
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {sortCampaigns(unorganizedWorkflows).map((workflow) => (
                                <>
                                  <TableRow key={workflow.id} className="hover:bg-muted/50">
                                    <TableCell className="font-medium">
                                      <div className="flex items-center gap-2">
                                        {workflow.type === 'email_drip' && <Mail className="h-4 w-4 text-muted-foreground" />}
                                        {workflow.type === 'sms_drip' && <MessageSquare className="h-4 w-4 text-muted-foreground" />}
                                        {workflow.type === 'web_to_lead' && <FormInput className="h-4 w-4 text-muted-foreground" />}
                                        {workflow.name}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="outline">
                                        {workflow.type === 'email_drip' && 'Email Drip'}
                                        {workflow.type === 'sms_drip' && 'SMS Drip'}
                                        {workflow.type === 'web_to_lead' && 'Web to Lead'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={workflow.isActive ? 'default' : 'secondary'}>
                                        {workflow.isActive ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>{new Date(workflow.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell className="text-right">
                                      <TooltipProvider>
                                        <div className="flex items-center justify-end space-x-2">
                                          {folders.length > 0 && (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => openMoveWorkflow(workflow)}
                                                  className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                                  data-testid={`button-move-${workflow.id}`}
                                                >
                                                  <MoveHorizontal className="h-4 w-4" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>Move to Campaign Folder</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          )}
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setSelectedWorkflow(selectedWorkflow?.id === workflow.id ? null : workflow)}
                                                className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                                data-testid={`button-view-steps-${workflow.id}`}
                                              >
                                                <Eye className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>{selectedWorkflow?.id === workflow.id ? 'Hide' : 'View'}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => navigate(`/campaign-builder?id=${workflow.id}`)}
                                                className="hover:!text-white hover:!bg-[#1d4c99]"
                                                data-testid={`button-edit-${workflow.id}`}
                                              >
                                                <Edit className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Edit</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </div>
                                      </TooltipProvider>
                                    </TableCell>
                                  </TableRow>
                                  {selectedWorkflow?.id === workflow.id && (
                                    <TableRow>
                                      <TableCell colSpan={5} className="p-0">
                                        <div className="bg-muted/30 p-6 border-t">
                                          {renderWorkflowBuilder(selectedWorkflow)}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </>
                              ))}
                            </TableBody>
                          </Table>
                        </Card>
                      ) : (
                        <p className="text-center text-muted-foreground py-8">
                          {folders.length > 0 
                            ? 'No unorganized campaigns. All campaigns are in folders.' 
                            : 'No campaigns created yet. Click "New Campaign" to get started.'}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {/* Folder Management Dialogs */}
          <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{selectedFolder ? 'Edit Folder' : 'Create New Folder'}</DialogTitle>
                <DialogDescription>
                  {selectedFolder ? 'Update folder details' : 'Create a new folder to organize your campaigns'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="folder-name">Folder Name</Label>
                  <Input
                    id="folder-name"
                    value={folderForm.name}
                    onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                    placeholder="Welcome Series"
                  />
                </div>
                <div>
                  <Label htmlFor="folder-description">Description (Optional)</Label>
                  <Textarea
                    id="folder-description"
                    value={folderForm.description}
                    onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                    placeholder="Campaigns for new customer onboarding"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsFolderDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleFolderSubmit} disabled={!folderForm.name}>
                    {selectedFolder ? 'Update' : 'Create'} Folder
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Move Campaign to Folder</DialogTitle>
                <DialogDescription>
                  Select a folder to move "{selectedWorkflowForMove?.name}" to
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => handleMoveWorkflow(null)}
                  >
                    <Folder className="h-4 w-4 mr-2" />
                    No Folder (Root)
                  </Button>
                  {folders.map((folder: any) => (
                    <Button
                      key={folder.id}
                      variant="outline"
                      className="justify-start"
                      onClick={() => handleMoveWorkflow(folder.id)}
                    >
                      <Folder className="h-4 w-4 mr-2" />
                      {folder.name}
                    </Button>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setIsMoveDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>


        {/* Trigger Links Tab */}
        <TabsContent value="links" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Trigger Links</h2>
              <p className="text-sm text-muted-foreground">
                Create trackable links that set merge fields and trigger workflows when clicked
              </p>
            </div>
            <Dialog open={isCreateLinkOpen} onOpenChange={setIsCreateLinkOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Link
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Trigger Link</DialogTitle>
                  <DialogDescription>
                    Create a trackable link that triggers workflows when clicked
                  </DialogDescription>
                </DialogHeader>
                <Form {...linkForm}>
                  <form onSubmit={linkForm.handleSubmit(onSubmitLink)} className="space-y-4">
                    <FormField
                      control={linkForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Link Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Interested in Health Plans" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={linkForm.control}
                      name="slug"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>URL Slug</FormLabel>
                          <FormControl>
                            <Input placeholder="health-plans-interest" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={linkForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Track interest in health insurance plans..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setIsCreateLinkOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={createLinkMutation.isPending}>
                        Create Link
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
            
            {/* Edit Link Dialog */}
            <Dialog open={!!editingLink} onOpenChange={(open) => !open && closeEditLink()}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Trigger Link</DialogTitle>
                  <DialogDescription>
                    Update the trigger link details
                  </DialogDescription>
                </DialogHeader>
                <Form {...linkForm}>
                  <form onSubmit={linkForm.handleSubmit(onSubmitLink)} className="space-y-4">
                    <FormField
                      control={linkForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Link Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Interested in Health Plans" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={linkForm.control}
                      name="slug"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>URL Slug</FormLabel>
                          <FormControl>
                            <Input placeholder="health-plans-interest" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={linkForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Track interest in health insurance plans..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={closeEditLink}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={updateLinkMutation.isPending}>
                        Update Link
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>

          {triggerLinks.length > 0 ? (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableLinkTableHead field="name" label="Name" />
                    <SortableLinkTableHead field="slug" label="Slug" />
                    <SortableLinkTableHead field="status" label="Status" />
                    <SortableLinkTableHead field="clicks" label="Clicks" />
                    <SortableLinkTableHead field="createdAt" label="Created" />
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortLinks(triggerLinks).map((link) => (
                    <TableRow key={link.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Link2 className="h-4 w-4 text-muted-foreground" />
                          {link.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="bg-muted px-2 py-1 rounded text-xs">
                          /trigger/{link.slug}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={link.isActive ? 'default' : 'secondary'}>
                          {link.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MousePointer className="h-4 w-4 text-muted-foreground" />
                          {link.clickCount}
                        </div>
                      </TableCell>
                      <TableCell>{new Date(link.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <TooltipProvider>
                          <div className="flex items-center justify-end space-x-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/trigger/${link.slug}`);
                                    toast({ title: "Link copied to clipboard" });
                                  }}
                                  className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                  data-testid={`button-copy-${link.id}`}
                                >
                                  <Link2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Copy Link</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditLink(link)}
                                  className="hover:!text-white hover:!bg-[#1d4c99]"
                                  data-testid={`button-edit-${link.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Edit</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Link2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No trigger links created yet</p>
              <p className="text-sm">Click "New Link" to create your first trigger link</p>
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold">Workflow Analytics</h2>
            <p className="text-sm text-muted-foreground">
              Track performance of your campaigns and trigger links
            </p>
          </div>

          <div className="grid gap-6">
            {/* Workflow Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Campaign Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {workflowAnalytics.length > 0 ? (
                  <div className="space-y-4">
                    {workflowAnalytics.map((analytics: any) => (
                      <div key={analytics.workflowId} className="border rounded-lg p-4">
                        <h4 className="font-medium mb-2">{analytics.workflowName}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-muted-foreground">Total Executions</div>
                            <div className="text-xl font-bold">{analytics.totalExecutions}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Completed</div>
                            <div className="text-xl font-bold text-green-600">{analytics.completedExecutions}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Active</div>
                            <div className="text-xl font-bold text-blue-600">{analytics.activeExecutions}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Failed</div>
                            <div className="text-xl font-bold text-red-600">{analytics.failedExecutions}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No campaign data available</p>
                    <p className="text-sm">Create and run campaigns to see analytics</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Form Analytics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FormInput className="h-5 w-5" />
                  Form Performance
                </CardTitle>
                <div className="mt-4">
                  <Select value={selectedFormId} onValueChange={setSelectedFormId}>
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue placeholder="Select a form" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Forms</SelectItem>
                      {leadForms.map((form: any) => (
                        <SelectItem key={form.id} value={form.id}>
                          {form.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const filteredForms = selectedFormId === 'all' 
                    ? leadForms 
                    : leadForms.filter((form: any) => form.id === selectedFormId);
                  
                  const totalPages = Math.ceil(filteredForms.length / FORMS_PER_PAGE);
                  const startIndex = (formAnalyticsPage - 1) * FORMS_PER_PAGE;
                  const endIndex = startIndex + FORMS_PER_PAGE;
                  const paginatedForms = filteredForms.slice(startIndex, endIndex);

                  return leadForms.length > 0 ? (
                    <>
                      <div className="space-y-4">
                        {paginatedForms.map((form: any) => {
                          const analytics = formAnalytics.find((a: any) => a.formId === form.id);
                          return (
                            <div key={form.id} className="border rounded-lg p-4">
                              <h4 className="font-medium mb-2">{form.name}</h4>
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <div className="text-muted-foreground">Impressions</div>
                                  <div className="text-xl font-bold">{analytics?.visitors || 0}</div>
                                </div>
                                <div>
                                  <div className="text-muted-foreground">Submissions</div>
                                  <div className="text-xl font-bold">{analytics?.submissions || 0}</div>
                                </div>
                                <div>
                                  <div className="text-muted-foreground">Completion Rate</div>
                                  <div className="text-xl font-bold">{analytics?.conversionRate || 0}%</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFormAnalyticsPage(Math.max(1, formAnalyticsPage - 1))}
                            disabled={formAnalyticsPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </Button>
                          <span className="text-sm text-muted-foreground">
                            Page {formAnalyticsPage} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFormAnalyticsPage(Math.min(totalPages, formAnalyticsPage + 1))}
                            disabled={formAnalyticsPage === totalPages}
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FormInput className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No forms available</p>
                      <p className="text-sm">Create web-to-lead forms to see analytics</p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Trigger Link Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="h-5 w-5" />
                  Trigger Link Performance
                </CardTitle>
                <div className="mt-4">
                  <Select value={selectedLinkId} onValueChange={setSelectedLinkId}>
                    <SelectTrigger className="w-full max-w-md">
                      <SelectValue placeholder="Select a trigger link" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Trigger Links</SelectItem>
                      {triggerLinkAnalytics.map((link: any) => (
                        <SelectItem key={link.linkId} value={link.linkId}>
                          {link.linkName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {triggerLinkAnalytics.length > 0 ? (
                  <div className="space-y-4">
                    {(selectedLinkId === 'all' 
                      ? triggerLinkAnalytics 
                      : triggerLinkAnalytics.filter((link: any) => link.linkId === selectedLinkId)
                    ).map((analytics: any) => (
                      <div key={analytics.linkId} className="border rounded-lg p-4">
                        <h4 className="font-medium mb-2">{analytics.linkName}</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          {window.location.origin}/trigger/{analytics.slug}
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-muted-foreground">Total Clicks</div>
                            <div className="text-xl font-bold">{analytics.totalClicks || 0}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Unique Clicks</div>
                            <div className="text-xl font-bold">{analytics.uniqueClicks || 0}</div>
                          </div>
                          <div>
                            <div className="text-muted-foreground">Last Clicked</div>
                            <div className="text-xl font-bold">
                              {analytics.lastClicked 
                                ? new Date(analytics.lastClicked).toLocaleDateString() 
                                : 'Never'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Link2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No trigger link data available</p>
                    <p className="text-sm">Create trigger links to see analytics</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}